import { useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { runInAction } from 'mobx';
import { useRouter } from 'next/router';
import styles from '@features/productPreview/sections/RecommendedProducts/RecommendedProducts.styles';
import { IRecommendedProductResponse } from '@features/productPreview/typescript';
import { WindowType } from '@typescript/index';
import SliderLinkedItems from '@components/SliderLinkedItems/SliderLinkedItems';
import ErrorBoundary from '@components/errors/ErrorBoundary';
import ProductState from '@features/categories/mobx/state/ProductState';
import NextProductApi from '@features/api/nextApiService/nextProductService';
import { useOffer } from '@hooks/useOffer';
import { PIMCORE_LOCALE_FALLBACK } from '@config/index';
import getPublicEnv from '@utils/getPublicEnv';
const PageSizeOnMobile = 4;
const PageSizeOnDesktop = 8;
type PropsType = {
  mainProductID: number;
  windowType: WindowType;
  isDecorativeArticle?: boolean;
  initialResponse?: IRecommendedProductResponse | null;
};
const RecommendedProducts = ({
  mainProductID,
  windowType,
  isDecorativeArticle = false,
  initialResponse = null
}: PropsType) => {
  const {
    t
  } = useTranslation('common');
  const [offerName, priceListName, storageName] = useOffer();
  const {
    locale
  } = useRouter();
  const createProducts = (response: IRecommendedProductResponse | null): ProductState[] => {
    const fetchProducts: ProductState[] = [];
    if (!response) return fetchProducts;
    const {
      products: newProducts
    } = response;
    if (!newProducts || newProducts.length === 0) return fetchProducts;
    runInAction(() => {
      newProducts.forEach(singleProduct => {
        fetchProducts.push(new ProductState(singleProduct));
      });
    });
    return fetchProducts;
  };
  const [products, setProducts] = useState<ProductState[]>(initialResponse ? createProducts(initialResponse) : []);
  const langLocale = locale ? PIMCORE_LOCALE_FALLBACK[locale] : PIMCORE_LOCALE_FALLBACK[getPublicEnv('NEXT_PUBLIC_DEFAULT_LOCALE')];
  const isMobile = windowType === WindowType.mobile;
  const isTablet = windowType === WindowType.tablet;
  const isSmallerThanDesktop = isTablet || isMobile;
  const pageSize = isSmallerThanDesktop ? PageSizeOnMobile : PageSizeOnDesktop;
  const fetchRecommendedProducts = async (currentOffset: number) => {
    const recommendedProductsResponse = await NextProductApi.getRecommendedProducts(mainProductID.toString(), offerName, priceListName, storageName, langLocale, currentOffset, pageSize, isDecorativeArticle);
    const preparedFetchProducts = createProducts(recommendedProductsResponse);
    const newProducts = currentOffset === 0 ? preparedFetchProducts : [...products, ...preparedFetchProducts];
    setProducts(newProducts);
  };
  const fetchAndSetRecommendedProducts = async (currentOffset = 0) => {
    await fetchRecommendedProducts(currentOffset);
  };
  useEffect(() => {
    if (initialResponse) {
      const preparedFetchProducts = createProducts(initialResponse);
      setProducts(preparedFetchProducts);
    } else {
      fetchAndSetRecommendedProducts();
    }
  }, [mainProductID]);
  const renderContent = () => products && products.length > 0 && <SliderLinkedItems products={products} title={t(isDecorativeArticle ? 'recommendedDecorativeArticlesTitle' : 'recommendedProductsTitle')} />;
  return products.length > 0 ? <ErrorBoundary>
      <section id="collection" sx={styles.section}>
        {renderContent()}
      </section>
    </ErrorBoundary> : null;
};
export default RecommendedProducts;